import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  BMark,
  BoomVerifiedBadge,
  BrandFooter,
  BrandHeader,
  RunClubBadge,
  SparkIcon,
  Sticker,
  Wordmark,
} from "@/components/runboom/marks";
import {
  connectStravaUrl,
  getLines,
  getMe,
  logout,
  syncStrava,
  type BestEffortGuess,
  type MeResponse,
  type PredictionLine,
  type RecentRun,
  type SyncResponse,
} from "@/lib/api";
import { formatDistance, formatPace, formatRaceTime } from "@/lib/format";
import { upcomingRaces } from "@/lib/mock";
import { cn } from "@/lib/utils";

const ME_HERO_IMG =
  "/brand/runner-profile-crop.png";

const DISTANCE_TONE: Record<string, string> = {
  "5K": "var(--color-bone)",
  "10K": "var(--color-acid-lime)",
  Half: "var(--color-lavender)",
  Marathon: "var(--color-electric-blue)",
  Comrades: "var(--color-blood-orange)",
};

const STADIUM_DISTANCES = ["5K", "10K", "Half", "Marathon", "Comrades"];

export const Route = createFileRoute("/me")({
  component: MePage,
});

function MePage() {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [lines, setLines] = useState<PredictionLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<SyncResponse | null>(null);
  const autoSyncedRef = useRef(false);

  const refresh = async () => {
    setError(null);
    const next = await getMe();
    const nextLines = next.authenticated
      ? await getLines().catch(() => [] as PredictionLine[])
      : [];
    setMe(next);
    setLines(nextLines);
    return next;
  };

  useEffect(() => {
    refresh()
      .catch((err) => setError(err instanceof Error ? err.message : "failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const runSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      const res = await syncStrava();
      setLastSync(res);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "sync failed");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (
      me?.authenticated &&
      me.bestEfforts.length === 0 &&
      !syncing &&
      !autoSyncedRef.current
    ) {
      autoSyncedRef.current = true;
      runSync();
    }
  }, [me, syncing]);

  const onDisconnect = async () => {
    await logout();
    autoSyncedRef.current = false;
    setLastSync(null);
    setLines([]);
    setLoading(true);
    await refresh().finally(() => setLoading(false));
  };

  return (
    <main className="mx-auto max-w-[1280px] px-4 py-4 sm:px-8 sm:py-10">
      <BrandHeader rightTab="Stadium" />

      <div className="mt-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <Wordmark size="sm" />
        </Link>
        <nav className="flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.1em] text-fg-muted sm:gap-5">
          <Link to="/oracle" className="hidden hover:text-fg sm:inline">Oracle</Link>
          <Link to="/races" className="hidden hover:text-fg sm:inline">Races</Link>
          <Link to="/wall" className="hidden hover:text-fg sm:inline">Wall</Link>
        </nav>
      </div>

      {loading ? (
        <MeSkeleton />
      ) : error && !me ? (
        <ErrorBlock message={error} />
      ) : me?.authenticated ? (
        <ConnectedPanel
          me={me}
          syncing={syncing}
          onSync={runSync}
          onDisconnect={onDisconnect}
          lastSync={lastSync}
          lines={lines}
        />
      ) : (
        <DisconnectedPanel mockMode={me?.mockMode ?? true} />
      )}

      {error && me ? (
        <p className="mt-4 font-mono text-[12px] uppercase tracking-[0.08em]" style={{ color: "var(--color-blood-orange)" }}>
          {error}
        </p>
      ) : null}

      <BrandFooter />
    </main>
  );
}

function MeSkeleton() {
  return (
    <section className="mt-6 flex flex-col gap-4 sm:mt-8 sm:gap-6">
      <div className="h-[280px] animate-pulse rounded-[22px] border border-border bg-bg-elevated sm:h-[360px]" />
      <div className="h-[180px] animate-pulse rounded-[18px] border border-border bg-bg-elevated" />
      <div className="h-[200px] animate-pulse rounded-[18px] border border-border bg-bg-elevated" />
    </section>
  );
}

function DisconnectedPanel({ mockMode }: { mockMode: boolean }) {
  return (
    <section className="mt-4 flex flex-col gap-4 sm:mt-8 sm:gap-8">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-[18px] border border-border bg-night shadow-[0_20px_90px_rgba(0,0,0,0.55)]">
        <HeroImage />
        <div className="relative grid grid-cols-1 gap-6 p-5 sm:p-10 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.12em]" style={{ color: "var(--color-blood-orange)" }}>
              <span className="inline-flex h-2 w-2 rounded-full" style={{ backgroundColor: "var(--color-blood-orange)" }} />
              Step 01 · One tap
            </div>
            <h1 className="mt-4 text-balance font-display-italic font-display text-[50px] leading-[43px] tracking-[0] text-fg sm:text-[88px] sm:leading-[76px] lg:text-[120px] lg:leading-[100px]">
              OPEN YOUR <span style={{ color: "var(--color-acid-lime)" }}>STADIUM.</span>
            </h1>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <a
                href={connectStravaUrl()}
                className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-[8px] px-6 py-3 font-mono text-[13px] uppercase tracking-[0.1em] text-night transition-transform active:scale-[0.96] sm:w-auto"
                style={{ backgroundColor: "var(--color-acid-lime)" }}
              >
                <StravaGlyph />
                Connect with Strava
              </a>
              {mockMode ? (
                <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-fg-subtle">
                  Demo mode — no credentials yet. Click anyway to walk the flow.
                </span>
              ) : null}
            </div>
          </div>

          <div className="relative hidden min-h-[320px] lg:block">
            <div className="absolute right-4 top-2">
              <BoomVerifiedBadge size={112} />
            </div>
            <div className="absolute right-10 top-36">
              <Sticker tone="lime" rotate={-6}>
                Every PB is a BOOM.
              </Sticker>
            </div>
            <div className="absolute bottom-2 right-8">
              <RunClubBadge size={88} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ConnectedPanel({
  me,
  syncing,
  onSync,
  onDisconnect,
  lastSync,
  lines,
}: {
  me: MeResponse;
  syncing: boolean;
  onSync: () => void;
  onDisconnect: () => void;
  lastSync: SyncResponse | null;
  lines: PredictionLine[];
}) {
  const runner = me.runner;
  const hasEfforts = me.bestEfforts.length > 0 || (lastSync?.bestEfforts.length ?? 0) > 0;

  return (
    <section className="mt-6 flex flex-col gap-6 sm:mt-8 sm:gap-8">
      {/* Identity hero */}
      <div className="relative overflow-hidden rounded-[18px] border border-border bg-night shadow-[0_20px_90px_rgba(0,0,0,0.55)]">
        <HeroImage />
        <div className="relative grid grid-cols-1 gap-5 p-5 sm:p-10 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.12em]" style={{ color: "var(--color-blood-orange)" }}>
              Runboom stadium · connected{me.mockMode ? " · demo" : ""}
            </div>
            <div className="mt-4 flex items-end gap-4">
              {runner?.avatar ? (
                <img
                  src={runner.avatar}
                  alt=""
                  className="h-16 w-16 rounded-[8px] object-cover outline outline-1 outline-white/10 sm:h-20 sm:w-20"
                />
              ) : null}
              <div className="min-w-0">
                {runner ? (
                  <div className="truncate font-mono text-[13px] tracking-[0.04em] text-fg-subtle">
                    @{runner.handle}
                  </div>
                ) : null}
              </div>
            </div>
            <h1 className="mt-4 text-balance font-display text-[40px] leading-[36px] tracking-[0] text-fg sm:text-[64px] sm:leading-[56px]">
              {runner?.displayName?.toUpperCase() ?? "WELCOME BACK."}
            </h1>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <button
                type="button"
                onClick={onSync}
                disabled={syncing}
                className={cn(
                  "inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-[8px] px-6 py-3 font-mono text-[13px] uppercase tracking-[0.1em] text-night transition-transform active:scale-[0.96] sm:w-auto",
                  syncing && "opacity-60",
                )}
                style={{ backgroundColor: "var(--color-acid-lime)" }}
              >
                <SparkIcon size={14} tone="bone" />
                {syncing ? "Syncing…" : "Sync activities"}
              </button>
              <button
                type="button"
                onClick={onDisconnect}
                className="min-h-12 rounded-[8px] border px-5 py-3 font-mono text-[12px] uppercase tracking-[0.1em] text-fg-muted transition-colors hover:text-blood-orange"
                style={{ borderColor: "var(--color-border-strong)" }}
              >
                Disconnect
              </button>
            </div>

            {syncing ? (
              <div
                className="mt-6 flex items-center gap-3 rounded-[12px] border px-4 py-3 font-mono text-[11px] uppercase tracking-[0.1em]"
                style={{
                  borderColor: "var(--color-electric-blue)",
                  backgroundColor: "rgba(0,97,255,0.08)",
                  color: "var(--color-electric-blue)",
                }}
              >
                <span className="inline-flex h-2 w-2 animate-pulse rounded-full" style={{ backgroundColor: "var(--color-electric-blue)" }} />
                {hasEfforts
                  ? "Refreshing your latest 200 Strava activities — filtering real runs now."
                  : "Pulling your latest 200 Strava activities — filtering real runs now."}
              </div>
            ) : null}
          </div>

          <div className="relative hidden min-h-[320px] lg:block">
            <div className="absolute right-2 top-0">
              <BMark size={180} />
            </div>
            <div className="absolute right-4 bottom-6">
              <Sticker tone="lavender" rotate={3}>
                Every PB is a BOOM.
              </Sticker>
            </div>
          </div>
        </div>
      </div>

      <LinesBlock lines={lines} syncing={syncing} />
      <BestEffortsBlock me={me} lastSync={lastSync} syncing={syncing} />
      <LatestRunsBlock me={me} lastSync={lastSync} />
    </section>
  );
}

function BestEffortsBlock({
  me,
  lastSync,
  syncing,
}: {
  me: MeResponse;
  lastSync: SyncResponse | null;
  syncing: boolean;
}) {
  const efforts = lastSync?.bestEfforts.length ? lastSync.bestEfforts : me.bestEfforts;
  const training = lastSync?.trainingSummary ?? me.trainingSummary;
  return (
    <div className="rounded-[18px] border border-border bg-bg-elevated p-5 sm:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-fg-subtle">
            PB board · from your Strava
          </div>
          <h3 className="mt-1 font-display text-[28px] leading-[28px] tracking-[0] text-fg sm:text-[36px] sm:leading-[36px]">
            FROM YOUR STRAVA
          </h3>
        </div>
        {lastSync ? (
          <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-fg-subtle">
            {lastSync.trainingSummary.runsCount}/{lastSync.activitiesCount} runs · synced{" "}
            {new Date(lastSync.syncedAt * 1000).toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        ) : null}
      </div>

      {training ? (
      <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <TrainingMetric
            label="Pulled"
            value={`${training.runsCount}/${training.activitiesCount} runs`}
            tone="var(--color-acid-lime)"
          />
          <TrainingMetric label="7d volume" value={formatKm(training.weeklyVolumeM)} />
          <TrainingMetric label="90d avg" value={`${formatKm(avgWeekly(training.ninetyDayVolumeM ?? 0, 90))}/wk`} />
          <TrainingMetric label="90d long" value={formatKm((training.longRun90dM ?? 0) || training.longRunM)} />
          <TrainingMetric label="90d climb" value={`${avgWeekly(training.ninetyDayElevationM ?? 0, 90)}m/wk`} />
          <TrainingMetric
            label={training.predictionReady ? "Oracle data" : "Needs data"}
            value={training.predictionReady ? "Ready" : `${training.readinessNotes.length} gaps`}
            tone={training.predictionReady ? "var(--color-acid-lime)" : "var(--color-blood-orange)"}
          />
        </div>
      ) : null}

      {syncing && efforts.length === 0 ? (
        <dl className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {STADIUM_DISTANCES.map((label) => (
            <div
              key={label}
              className="border-l-2 pl-3"
              style={{ borderColor: DISTANCE_TONE[label] }}
            >
              <div
                className="font-mono text-[11px] uppercase tracking-[0.1em]"
                style={{ color: DISTANCE_TONE[label] }}
              >
                {displayDistance(label)}
              </div>
              <div className="mt-1 h-9 w-24 animate-pulse rounded bg-bg-sunken" />
              <div className="mt-1 h-3 w-16 animate-pulse rounded bg-bg-sunken" />
            </div>
          ))}
        </dl>
      ) : efforts.length === 0 ? (
        <p className="mt-5 font-mono text-[12px] uppercase tracking-[0.08em] text-fg-muted">
          No race-distance efforts in your last 200 activities yet.
          Sync again after your next long run.
        </p>
      ) : (
        <dl className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-5">
          {effortSlots(efforts).map(({ label, effort }) => (
            <div
              key={label}
              className="border-l-2 pl-3"
              style={{ borderColor: DISTANCE_TONE[label] ?? "var(--color-bone)" }}
            >
              <dt
                className="font-mono text-[11px] uppercase tracking-[0.1em]"
                style={{ color: DISTANCE_TONE[label] ?? "var(--color-bone)" }}
              >
                {displayDistance(label)}
              </dt>
              {effort ? (
                <>
                  <dd
                    className="mt-1 font-display tabular text-[34px] leading-none tracking-[0] sm:text-[44px]"
                    style={{ color: DISTANCE_TONE[label] ?? "var(--color-bone)" }}
                  >
                    {formatRaceTime(effort.time_seconds)}
                  </dd>
                  <dd className="mt-1 font-mono text-[11px] uppercase tracking-[0.1em] text-fg-subtle">
                    PB · {new Date(effort.set_at).toLocaleDateString("en-GB", { month: "short", year: "numeric" }).toUpperCase()}
                  </dd>
                </>
              ) : (
                <>
                  <dd className="mt-1 font-display tabular text-[34px] leading-none tracking-[0] text-fg-muted sm:text-[44px]">
                    --
                  </dd>
                  <dd className="mt-1 font-mono text-[11px] uppercase tracking-[0.1em] text-fg-subtle">
                    Needs race effort
                  </dd>
                </>
              )}
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

function TrainingMetric({
  label,
  value,
  tone = "var(--color-fg)",
}: {
  label: string;
  value: string;
  tone?: string;
}) {
  return (
    <div className="rounded-[10px] border border-border bg-bg-sunken px-3 py-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-fg-subtle">
        {label}
      </div>
      <div
        className="mt-1 truncate font-mono text-[13px] uppercase tracking-[0.08em]"
        style={{ color: tone }}
      >
        {value}
      </div>
    </div>
  );
}

function LinesBlock({
  lines,
  syncing,
}: {
  lines: PredictionLine[];
  syncing: boolean;
}) {
  if (lines.length === 0) {
    if (syncing) {
      return (
        <div className="rounded-[18px] border border-border bg-bg-elevated p-5 sm:p-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-fg-subtle">
            Stadium lines
          </div>
          <p className="mt-3 font-mono text-[12px] uppercase tracking-[0.08em] text-fg-muted">
            The Oracle is turning your Strava history into five race previews.
          </p>
        </div>
      );
    }
    return null;
  }

  const source = lines[0]?.source;
  const modelVersion = lines[0]?.modelVersion;

  return (
    <div className="rounded-[14px] border border-white/10 bg-[rgba(18,20,23,0.92)] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.32)] sm:p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-subtle">
            Race predictions
          </div>
          <h3 className="mt-1 font-display text-[26px] leading-[26px] tracking-[0] text-fg sm:text-[34px] sm:leading-[34px]">
            RACE PICKS
          </h3>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {modelVersion ? (
            <span className="rounded-[8px] border border-white/10 bg-white/[0.04] px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-fg-subtle">
              {modelVersion}
            </span>
          ) : null}
          <span
            className="rounded-[8px] border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.08em]"
            style={
              source === "strava"
                ? {
                    borderColor: "rgba(204,255,0,0.45)",
                    color: "var(--color-acid-lime)",
                    backgroundColor: "rgba(204,255,0,0.06)",
                  }
                : { borderColor: "var(--color-border-strong)", color: "var(--color-fg-subtle)" }
            }
          >
            {source === "strava" ? "Live Strava" : "Seeded"}
          </span>
        </div>
      </div>

      <ul className="mt-4 grid grid-cols-1 gap-2 lg:grid-cols-5">
        {orderLines(lines).map((line) => {
          const race = raceForLine(line);
          return (
          <li
            key={line.raceSlug}
            className="rounded-[10px] border border-white/10 bg-black/35 p-3 shadow-[0_10px_28px_rgba(0,0,0,0.22)]"
          >
            <div className="flex min-h-0 flex-col gap-3 lg:min-h-[228px]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: DISTANCE_TONE[line.distanceLabel] ?? "var(--color-fg)" }}
                    />
                    <div className="font-mono text-[11px] uppercase tracking-[0.08em] text-fg">
                      {displayDistance(line.distanceLabel)}
                    </div>
                  </div>
                  <Link
                    to="/races/$slug"
                    params={{ slug: line.raceSlug }}
                    className="mt-1 block truncate font-mono text-[10px] uppercase tracking-[0.07em] text-fg-subtle hover:text-acid-lime"
                  >
                    {line.raceName.toUpperCase()}
                  </Link>
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-display tabular text-[30px] leading-none tracking-[0] text-fg sm:text-[34px]">
                    {formatRaceTime(line.p50Seconds)}
                  </div>
                  <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.06em] text-fg-subtle">
                    {line.boomProbabilityPct}% boom
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <MiniStat label="Low" value={formatRaceTime(line.p10Seconds)} />
                <MiniStat label="High" value={formatRaceTime(line.p90Seconds)} />
              </div>

              <div className="space-y-1.5">
                {topDrivers(line).map((driver) => (
                  <div
                    key={driver.label}
                    className="grid grid-cols-[1fr_auto] gap-3 rounded-[8px] bg-white/[0.035] px-2.5 py-2 font-mono text-[9px] uppercase tracking-[0.06em]"
                  >
                    <span className="truncate text-fg-subtle">{shortDriverLabel(driver.label)}</span>
                    <span
                      className="tabular"
                      style={{
                        color:
                          driver.deltaSeconds < 0
                            ? "var(--color-acid-lime)"
                            : driver.deltaSeconds > 0
                              ? "var(--color-blood-orange)"
                              : "var(--color-fg-subtle)",
                      }}
                    >
                      {formatDelta(driver.deltaSeconds)}
                    </span>
                  </div>
                ))}
              </div>

              {race?.entryUrl ? (
                <a
                  href={race.entryUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-auto flex min-h-9 items-center justify-center rounded-[8px] border border-white/10 px-3 font-mono text-[10px] uppercase tracking-[0.08em] text-fg transition-colors hover:border-acid-lime hover:text-acid-lime active:scale-[0.96]"
                >
                  Enter
                </a>
              ) : null}
            </div>
          </li>
        )})}
      </ul>
    </div>
  );
}

function LatestRunsBlock({
  me,
  lastSync,
}: {
  me: MeResponse;
  lastSync: SyncResponse | null;
}) {
  const runs = lastSync?.latestRuns.length ? lastSync.latestRuns : me.latestRuns;
  const training = lastSync?.trainingSummary ?? me.trainingSummary;
  if (runs.length === 0) return null;

  return (
    <div className="rounded-[18px] border border-border bg-bg-elevated p-5 sm:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-fg-subtle">
            Sync proof
          </div>
          <h3 className="mt-1 font-display text-[28px] leading-[28px] tracking-[0] text-fg sm:text-[36px] sm:leading-[36px]">
            LATEST RUNS USED
          </h3>
        </div>
        {training ? (
          <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-fg-subtle">
            {training.runsCount} runs found from {training.activitiesCount} activities
          </span>
        ) : null}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
        {runs.slice(0, 8).map((run) => (
          <RunFeedRow key={run.id} run={run} />
        ))}
      </div>
    </div>
  );
}

function RunFeedRow({ run }: { run: RecentRun }) {
  return (
    <div className="rounded-[10px] border border-border bg-bg-sunken px-3 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-mono text-[11px] uppercase tracking-[0.08em] text-fg">
            {run.name}
          </div>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-fg-subtle">
            {new Date(run.start_date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
            })}
          </div>
        </div>
        <div className="shrink-0 text-right font-mono text-[11px] uppercase tracking-[0.08em] text-acid-lime">
          {formatDistance(run.distance_m)}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 font-mono text-[10px] uppercase tracking-[0.08em] text-fg-subtle">
        <span className="tabular">{formatRaceTime(run.moving_time_seconds)}</span>
        <span className="tabular">{formatPace(run.pace_seconds_per_km)}</span>
        <span className="tabular">{run.elevation_gain_m}m</span>
      </div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[8px] border border-white/10 bg-white/[0.03] px-2.5 py-2">
      <div className="font-mono text-[9px] uppercase tracking-[0.08em] text-fg-subtle">
        {label}
      </div>
      <div className="mt-0.5 font-mono text-[11px] uppercase tracking-[0.04em] text-fg">
        {value}
      </div>
    </div>
  );
}


function raceForLine(line: PredictionLine) {
  return upcomingRaces.find((race) => race.slug === line.raceSlug);
}

function displayDistance(label: string) {
  if (label === "Half") return "21.1K";
  if (label === "Marathon") return "42.2K";
  return label.toUpperCase();
}

function formatKm(metres: number) {
  return `${(metres / 1000).toFixed(1)}km`;
}

function avgWeekly(totalMetres: number, days: number) {
  return Math.round(totalMetres / (days / 7));
}

function topDrivers(line: PredictionLine) {
  const preferred = line.drivers.filter((driver) =>
    /Speed anchor|Anchor recency|90d volume|90d hill prep|90d long run|Run frequency|Course|Forecast/.test(
      driver.label,
    ),
  );
  return preferred.slice(0, 4);
}

function shortDriverLabel(label: string) {
  return label
    .replace("Speed anchor: ", "Anchor ")
    .replace("Fitness trend ", "Fitness ")
    .replace("Anchor recency ", "Recency ")
    .replace("90d volume ", "Volume ")
    .replace("90d hill prep ", "Hills ")
    .replace("90d long run ", "Long ")
    .replace("Run frequency ", "Frequency ")
    .replace("Forecast ", "Weather ");
}

function formatDelta(seconds: number) {
  if (seconds === 0) return "base";
  const sign = seconds > 0 ? "+" : "";
  return `${sign}${formatRaceTime(seconds)}`;
}

function effortSlots(efforts: BestEffortGuess[]) {
  return STADIUM_DISTANCES.map((label) => ({
    label,
    effort: efforts.find((effort) => effort.label === label),
  }));
}

function orderLines(lines: PredictionLine[]) {
  return [...lines].sort(
    (a, b) => STADIUM_DISTANCES.indexOf(a.distanceLabel) - STADIUM_DISTANCES.indexOf(b.distanceLabel),
  );
}

function ErrorBlock({ message }: { message: string }) {
  return (
    <div
      className="mt-6 rounded-[18px] border p-5 font-mono text-[12px] uppercase tracking-[0.1em] sm:mt-8"
      style={{
        borderColor: "var(--color-blood-orange)",
        color: "var(--color-blood-orange)",
        backgroundColor: "rgba(255,59,0,0.06)",
      }}
    >
      Couldn't reach the API · {message}
    </div>
  );
}

function HeroImage() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <img
        src={ME_HERO_IMG}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-[64%_50%] opacity-80 outline outline-1 outline-white/10"
        style={{
          maskImage:
            "linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.2) 35%, rgba(0,0,0,1) 75%)",
          WebkitMaskImage:
            "linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.2) 35%, rgba(0,0,0,1) 75%)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-night via-night/85 to-transparent" />
    </div>
  );
}

function StravaGlyph() {
  return (
    <svg aria-hidden width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
    </svg>
  );
}
